/**
 * JSON schemas + default values for the per-org auto-detect classifier
 * configuration vocabulary. Used by:
 * - the seed migration (0147) that creates the underlying ConfigurationItem rows
 * - the server-side accessor (getAutoDetectConfig) for value parsing + validation
 * - the admin UI that renders typed inputs against these schemas
 *
 * Defaults preserve current behaviour: `skipCategories` mirrors today's
 * pre-filter set, sender allow/blocklist are empty, lookback is 90 days,
 * and the cache + tier-downgrade overrides are `null` (use the existing
 * PostHog flag fall-through).
 */

import { MessageClassificationSkipCategory } from '../../prisma/enums';

/**
 * Skip-category set the pre-filter is allowed to act on. An entry present
 * here = "skip emails matching this category"; an entry absent = "send to
 * the LLM regardless of pre-filter match for this category".
 *
 * `DUPLICATE` and `SEMANTIC_DUPLICATE` are intentionally NOT included in
 * the default — those categories come from the dedup paths, not the
 * pre-filter, and disabling them via this setting would silently disable
 * duplicate detection (a correctness concern, not a noise filter).
 * `AUTOMATED_REPORT` is likewise not a pre-filter category: Curium's own
 * reports are skipped by a fixed rule before the pre-filter runs.
 */
export const AUTO_DETECT_DEFAULT_SKIP_CATEGORIES: ReadonlyArray<MessageClassificationSkipCategory> = [
    MessageClassificationSkipCategory.AUTO_REPLY,
    MessageClassificationSkipCategory.NEWSLETTER,
    MessageClassificationSkipCategory.DELIVERY_FAILURE,
    MessageClassificationSkipCategory.OUT_OF_OFFICE,
    MessageClassificationSkipCategory.SPAM_PATTERN,
    MessageClassificationSkipCategory.MARKETING,
];

/** Hard caps for admin-supplied input. Tighter caps reduce ReDoS surface. */
export const AUTO_DETECT_LIMITS = {
    SENDER_LIST_MAX: 200,
    SENDER_ENTRY_MAX_LENGTH: 254,
    SUBJECT_REGEX_MAX_LENGTH: 200,
    SUBJECT_REGEX_MAX_COUNT: 30,
    BODY_PHRASE_MIN_LENGTH: 4,
    BODY_PHRASE_MAX_LENGTH: 100,
    BODY_PHRASE_MAX_COUNT: 30,
    DUPLICATE_LOOKBACK_HOURS_MIN: 24,
    DUPLICATE_LOOKBACK_HOURS_MAX: 8760,
    DUPLICATE_LOOKBACK_HOURS_DEFAULT: 2160,
} as const;

const SENDER_PATTERN = '^([^@\\s]+@)?[^\\s]+$';

export const AUTO_DETECT_SCHEMAS = {
    skipCategories: {
        type: 'array',
        items: { enum: Object.values(MessageClassificationSkipCategory) },
        uniqueItems: true,
        default: AUTO_DETECT_DEFAULT_SKIP_CATEGORIES,
    },
    senderAllowlist: {
        type: 'array',
        items: {
            type: 'string',
            minLength: 3,
            maxLength: AUTO_DETECT_LIMITS.SENDER_ENTRY_MAX_LENGTH,
            pattern: SENDER_PATTERN,
        },
        maxItems: AUTO_DETECT_LIMITS.SENDER_LIST_MAX,
        default: [],
    },
    senderBlocklist: {
        type: 'array',
        items: {
            type: 'string',
            minLength: 3,
            maxLength: AUTO_DETECT_LIMITS.SENDER_ENTRY_MAX_LENGTH,
            pattern: SENDER_PATTERN,
        },
        maxItems: AUTO_DETECT_LIMITS.SENDER_LIST_MAX,
        default: [],
    },
    extraComplaintPatterns: {
        type: 'object',
        additionalProperties: false,
        properties: {
            // IMPORTANT: subject patterns are validated as regex; admin-supplied
            // input. Body cues are LITERAL phrases only — never compiled to regex
            // on the body path. Use `string.includes()` over `RegExp.test()` to
            // avoid ReDoS on long email bodies.
            subject: {
                type: 'array',
                items: { type: 'string', maxLength: AUTO_DETECT_LIMITS.SUBJECT_REGEX_MAX_LENGTH },
                maxItems: AUTO_DETECT_LIMITS.SUBJECT_REGEX_MAX_COUNT,
            },
            body: {
                type: 'array',
                items: {
                    type: 'string',
                    minLength: AUTO_DETECT_LIMITS.BODY_PHRASE_MIN_LENGTH,
                    maxLength: AUTO_DETECT_LIMITS.BODY_PHRASE_MAX_LENGTH,
                },
                maxItems: AUTO_DETECT_LIMITS.BODY_PHRASE_MAX_COUNT,
            },
        },
        default: { subject: [], body: [] },
    },
    duplicateLookbackHours: {
        type: 'integer',
        minimum: AUTO_DETECT_LIMITS.DUPLICATE_LOOKBACK_HOURS_MIN,
        maximum: AUTO_DETECT_LIMITS.DUPLICATE_LOOKBACK_HOURS_MAX,
        default: AUTO_DETECT_LIMITS.DUPLICATE_LOOKBACK_HOURS_DEFAULT,
    },
    /**
     * Tri-state: null = use the existing PostHog feature flag fall-through;
     * true = force on; false = force off. Preserves the current PostHog
     * rollout state until an org explicitly migrates.
     */
    semanticCacheOverride: {
        type: ['boolean', 'null'],
        default: null,
    },
    tierDowngradeOverride: {
        type: ['boolean', 'null'],
        default: null,
    },
} as const;

export type AutoDetectSkipCategoriesValue = MessageClassificationSkipCategory[];
export type AutoDetectSenderListValue = string[];
export type AutoDetectExtraComplaintPatternsValue = { subject: string[]; body: string[] };
export type AutoDetectDuplicateLookbackHoursValue = number;
export type AutoDetectOverrideValue = boolean | null;

/** Limits for the auto-detect internal-email-domains list (Phase 2.5). */
export const AUTO_DETECT_INTERNAL_DOMAINS_LIMITS = {
    LIST_MAX: 25,
    ENTRY_MIN_LENGTH: 3,
    ENTRY_MAX_LENGTH: 254,
} as const;

const INTERNAL_DOMAIN_PATTERN = '^[a-z0-9.-]+\\.[a-z]{2,}$';

/**
 * JSON schema for `AUTO_DETECT_INTERNAL_EMAIL_DOMAINS`. Each entry is a bare
 * domain (no `@`, no path); used by `computeMessageDirection` to derive
 * whether each address is on the org's own domain. Empty by default — when
 * the per-org overlay is empty, `getAutoDetectConfig` falls back to
 * `Org.authenticatedDomain` so direction labelling works without an
 * explicit settings visit for orgs that have completed SendGrid sender
 * authentication.
 */
export const AUTO_DETECT_INTERNAL_DOMAINS_SCHEMA = {
    type: 'array',
    items: {
        type: 'string',
        minLength: AUTO_DETECT_INTERNAL_DOMAINS_LIMITS.ENTRY_MIN_LENGTH,
        maxLength: AUTO_DETECT_INTERNAL_DOMAINS_LIMITS.ENTRY_MAX_LENGTH,
        pattern: INTERNAL_DOMAIN_PATTERN,
    },
    maxItems: AUTO_DETECT_INTERNAL_DOMAINS_LIMITS.LIST_MAX,
    uniqueItems: true,
    default: [],
} as const;

export type AutoDetectInternalDomainsValue = string[];
